import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useSortingAccessProfilesTableVisitorGroup, {
  createUserGroupsPrioritySorting
} from 'hooks/useSorting/useSortingAccessProfiles/VisitorGroups/useSortingAccessProfilesTableVisitorGroup';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { VisitorsGroupsWithAccessGrantsState } from 'state/types';
import { Props as IconProps } from 'ui/atoms/Icon/Icon';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableRow from 'ui/molecules/TableRow/TableRow';
import SortingMenu from 'ui/organisms/SortingMenu/SortingMenu';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import ArrayUtil from 'utils/Array/Array.util';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import getVisitorGroupIcon from 'utils/VisitorGroupIcon/VisitorGroupIcon.util';
import {
  ARROW_DOWN_HEIGHT,
  ARROW_DOWN_WIDTH,
  ARROW_RIGHT_HEIGHT,
  ARROW_RIGHT_WIDTH,
  SecondLineText,
  Variant
} from '../shared';

export interface Props {
  id: string;
  loading: boolean;
  disabled: boolean;
  isCollapsed: boolean;
  handleCollapse: () => void;
  variant: Variant;
  filteredVisitorGroups: VisitorsGroupsWithAccessGrantsState;
  visitorGroups: VisitorsGroupsWithAccessGrantsState;
  selectedIds: string[];
  disabledIds: string[];
  additionalTexts: SecondLineText[];
  warningTexts: SecondLineText[];
  handleClickTableRow: (group: VisitorGroup) => void;
}

const AccessProfilesVisitorGroups: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  loading,
  disabled,
  isCollapsed,
  handleCollapse,
  variant,
  filteredVisitorGroups,
  visitorGroups,
  selectedIds,
  disabledIds,
  additionalTexts,
  warningTexts,
  handleClickTableRow
}) => {
  const { translation } = useTranslation();
  const { sorting, handleOpenSortingMenu, handleSortingOnChange, sortingMenuRef } =
    useSortingAccessProfilesTableVisitorGroup({
      translation,
      visitorGroups,
      selectedIds
    });
  const skeletonArray = ArrayUtil.SkeletonArray();

  const isVisitorGroupSelected = (visitorGroupId: string) => selectedIds.includes(visitorGroupId);
  const isVisitorGroupDisabled = (visitorGroupId: string) => disabledIds.includes(visitorGroupId);

  const getVisitorGroupWithAdditionalText = (visitorGroupId: string) =>
    additionalTexts.find((item) => item.id === visitorGroupId)?.text;

  const getVisitorGroupWithWarningText = (visitorGroupId: string) =>
    warningTexts.find((item) => item.id === visitorGroupId)?.text;

  const getSecondLineText = (visitorGroupId: string) =>
    getVisitorGroupWithAdditionalText(visitorGroupId) || getVisitorGroupWithWarningText(visitorGroupId);

  const getIconAndSecondLineTextColor = (visitorGroupId: string) =>
    getVisitorGroupWithWarningText(visitorGroupId) ? 'warning' : 'lTextHigh';

  const getFirstLineTextColor = (visitorGroupId: string) =>
    variant === 'icon' && isVisitorGroupSelected(visitorGroupId) && !isVisitorGroupDisabled(visitorGroupId)
      ? 'primary'
      : 'lTextHigh';

  const getBeforeContentIconProps = (visitorGroup: VisitorGroup): IconProps => ({
    name: getVisitorGroupIcon.getVisitorGroupIcon(visitorGroup.type as VisitorGroupType),
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    color: getIconAndSecondLineTextColor(visitorGroup.id),
    stroke: 'transparent'
  });

  return (
    <Table
      id={id}
      height={isCollapsed ? '3rem' : 'auto'}
      flex="0 1 auto"
      header={
        !loading ? (
          <ComponentWrapper flexDirection="column">
            <SortingMenu
              ref={sortingMenuRef}
              isOpen={sorting.isMenuOpen}
              sortingName={translation.alphabetic_order}
              sortingOptions={[
                {
                  isSelected: !!sorting.isAlphabeticAscending,
                  onClick: () => handleSortingOnChange(sorting.priority, true),
                  text: translation.ascending,
                  id: 'sorting-option-ascending'
                },
                {
                  isSelected: !sorting.isAlphabeticAscending,
                  onClick: () => handleSortingOnChange(sorting.priority, false),
                  text: translation.descending,
                  id: 'sorting-option-descending'
                }
              ]}
              additionalSortingName={translation.priority}
              additionalSortingOptions={
                !loading && [
                  ...createUserGroupsPrioritySorting(translation).map((priority) => ({
                    isSelected: sorting.priority === priority,
                    onClick: () => handleSortingOnChange(priority, sorting.isAlphabeticAscending),
                    text: priority,
                    id: `sorting-option-${priority}`
                  })),
                  variant === 'switch' && !disabled
                    ? {
                        isSelected: sorting.priority === translation.selected,
                        onClick: () => handleSortingOnChange(translation.selected, sorting.isAlphabeticAscending),
                        text: translation.selected,
                        id: 'sorting-option-selected'
                      }
                    : undefined
                ]
              }
            >
              <TableHeaderRow
                disabled={disabled}
                id="header-user-groups"
                iconAfterHeaderProps={{
                  name: 'Filter',
                  id: 'sort-visitor-groups-button',
                  onClick: !disabled ? handleOpenSortingMenu : undefined
                }}
                iconAfterHeaderBorder
                iconBeforeHeaderProps={{
                  name: isCollapsed ? 'ArrowRight' : 'ArrowDown',
                  width: isCollapsed ? ARROW_RIGHT_WIDTH : ARROW_DOWN_WIDTH,
                  height: isCollapsed ? ARROW_RIGHT_HEIGHT : ARROW_DOWN_HEIGHT,
                  color: isCollapsed ? undefined : 'transparent',
                  stroke: isCollapsed ? undefined : 'lTextMedium',
                  viewBox: isCollapsed ? '0 0 22 45' : undefined,
                  onClick: handleCollapse
                }}
              >
                <TableHeader
                  headerText={translation.user_groups}
                  superText={variant === 'switch' ? `(${selectedIds.length})` : undefined}
                  headerTextOpacity={disabled ? '0.3' : '1'}
                  id="access-profiles-header-user-groups"
                />
              </TableHeaderRow>
            </SortingMenu>
          </ComponentWrapper>
        ) : (
          <ComponentWrapper flexDirection="column">
            <TableHeaderRow disabled={disabled} id="access-profiles-header-row-user-groups">
              <TableHeader
                headerText={translation.user_groups}
                iconSorting="ArrowDownSorting"
                onClick={handleOpenSortingMenu}
                id="access-profiles-header-user-groups"
              />
            </TableHeaderRow>
          </ComponentWrapper>
        )
      }
    >
      {loading
        ? skeletonArray.map((skeletonItem) => (
            <TableRow
              beforeSwitchSkeleton={variant === 'switch'}
              avatarSkeleton={variant === 'icon'}
              selected={false}
              disabled
              id="row-user-groups-loading"
              key={`skeletonTableRow-${skeletonItem.id}`}
            >
              <TableCell isLoading firstLineText="" />
            </TableRow>
          ))
        : !isCollapsed &&
          filteredVisitorGroups.map((group, index) => (
            <TableRow
              hoverEffect
              afterSwitchProps={
                variant !== 'switch'
                  ? undefined
                  : {
                      checked: isVisitorGroupSelected(group.id),
                      disabled: isVisitorGroupDisabled(group.id),
                      onClick: (event) => event.stopPropagation()
                    }
              }
              marginAfterContent={variant === 'switch'}
              beforeContentIconProps={getBeforeContentIconProps(group)}
              selected={variant === 'icon' ? isVisitorGroupSelected(group.id) : false}
              disabled={(variant === 'switch' && disabled) || isVisitorGroupDisabled(group.id)}
              onClick={!disabled && !isVisitorGroupDisabled(group.id) ? () => handleClickTableRow(group) : undefined}
              afterContentIconTooltipPosition={{
                top: filteredVisitorGroups.length === index + 1 ? undefined : '0',
                right: '0',
                bottom: filteredVisitorGroups.length === index + 1 ? '0' : undefined,
                left: undefined
              }}
              id={`row-user-group-${index}`}
              key={group.id}
            >
              <TableCell
                firstLineText={group.name}
                firstLineColor={getFirstLineTextColor(group.id)}
                secondLineText={getSecondLineText(group.id)}
                secondLineColor={getIconAndSecondLineTextColor(group.id)}
                key={group.id}
                isRowWithIconAndSwitch
              />
            </TableRow>
          ))}
    </Table>
  );
};

export default AccessProfilesVisitorGroups;
